import { AutoAwesome, DeleteForeverOutlined, EditOutlined, GroupOutlined } from "@mui/icons-material"
import { Box } from "@mui/material"
import { intl } from "src/internationalization/intl"
import { useDrawerStore, useMessageStore, useOpportunitiesStore } from "src/store"
import { useModalStore } from "src/store/modalStore"
import { OpportunitiesState } from "src/store/opportunitiesStore/types"
import { Button, Grid, IconButton, Typography } from "../../basics"
import { StyledObjectiveCard } from "./styled"
import { IObjetiveCardProps } from "./types"

const ObjectiveCard = (props: IObjetiveCardProps) => {
    const showSeeClientsButton = props.showSeeClientsButton ?? true
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const findOpportunityByName = useOpportunitiesStore((s: OpportunitiesState) => s.findOpportunityByName)
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)
    const setOpen = useModalStore((s: any) => s.setOpen)

    const handleEditObjective = () => {
        setOpen("editObjectiveModal", {
            objectiveId: props.objectiveId,
            title: props.title,
            createdDate: props.createdDate ?? "",
            endDate: props.endDate ?? props.dueDateIso ?? "",
        })
    }
    const handleDeleteObjective = () => {
        setOpen("deleteObjectiveModal", {
            objectiveId: props.objectiveId,
            objectiveTitle: props.title,
        })
    }
    const handleClickAIButton = async () => {
        let opportunityData = findOpportunityByName(props.title)
        if (actualContext !== props.context) {
            try {
                setJsonContext(opportunityData)
                setActualContext(props.context)
            } catch (error) {
                console.error("Error setting context:", error)
            }
        }
        openDrawer()
    }

    const handleClickSeeClients = () => {
        if (props.impactedClientsArray && props.impactedClientsArray.length > 0) {
            setOpen("impactedClientsModal", {
                impactedClients: props.impactedClientsArray,
                opportunityTitle: props.title,
                recommendation: props.recommendation,
                addedAt: props.addedAt,
                dueDate: props.dueDate,
                impactedClientsCount: props.impactedClients,
            })
        }
    }

    const stretchCard = props.fullHeight === true
    const compact = props.compact === true

    // Headline = título; descrição = texto da recomendação (quando diferente do título).
    const titleTrim = (props.title ?? "").trim()
    const recommendationTrim = (props.recommendation ?? "").trim()
    const headline = titleTrim || recommendationTrim
    const description =
        titleTrim && recommendationTrim && titleTrim.toLowerCase() !== recommendationTrim.toLowerCase()
            ? recommendationTrim
            : titleTrim
                ? ""
                : ""

    // Sistema de urgência: vermelho ≤7d, dourado 8–15d, verde >15d, vencida.
    const urgency = (() => {
        if (props.isExpired) {
            const overdue = props.dueInDays != null ? Math.abs(props.dueInDays) : null
            return {
                color: "accentError.main",
                label:
                    overdue != null
                        ? intl("overdueByDaysLabel", { values: { days: overdue } })
                        : intl("expiredBadge"),
            }
        }
        if (props.dueInDays == null) return null
        const d = props.dueInDays
        const color = d <= 7 ? "accentError.main" : d <= 15 ? "secondary.main" : "accentSuccess.main"
        const label =
            d <= 0
                ? intl("dueToday")
                : d === 1
                    ? intl("dueTomorrow")
                    : intl("dueInDaysLabel", { values: { days: d } })
        return { color, label }
    })()

    const canManage = props.showManageActions === true

    return (
        <StyledObjectiveCard
            fullWidth={props.fullWidth}
            fullHeight={props.fullHeight}
            compact={compact}
            selected={props.selected}
        >
            <Grid
                container
                direction="column"
                justifyContent={stretchCard ? "space-between" : "flex-start"}
                sx={{ height: stretchCard ? "100%" : "auto" }}
            >
                {/* Cabeçalho: título + ação de excluir (gerente) */}
                <Grid item>
                    <Grid
                        container
                        wrap="nowrap"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        columnSpacing={0.5}
                    >
                        <Grid item xs zeroMinWidth>
                            <Typography
                                variant={compact ? "subtitle2" : undefined}
                                sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                }}
                            >
                                {headline}
                            </Typography>
                        </Grid>
                        {props.showImpactedClients && canManage && (
                            <Grid
                                item
                                sx={{
                                    flexShrink: 0,
                                    ml: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0,
                                }}
                            >
                                <IconButton
                                    onClick={handleEditObjective}
                                    variant="clean"
                                    color="neutral"
                                    size="small"
                                    aria-label={intl("editObjectiveAria")}
                                >
                                    <EditOutlined sx={{ fontSize: compact ? 16 : 18, opacity: 0.72 }} />
                                </IconButton>
                                <IconButton
                                    onClick={handleDeleteObjective}
                                    variant="clean"
                                    color="neutral"
                                    size="small"
                                >
                                    <DeleteForeverOutlined sx={{ fontSize: compact ? 17 : 20 }} />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                    {description ? (
                        <Typography
                            variant="caption"
                            color="quaternary"
                            sx={{
                                display: "-webkit-box",
                                mt: 0.5,
                                lineHeight: 1.35,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {description}
                        </Typography>
                    ) : null}
                </Grid>

                {/* Meta: urgência + clientes impactados */}
                <Grid item>
                    {urgency ? (
                        <Grid sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: urgency.color,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography variant="caption" sx={{ color: urgency.color, fontWeight: 700 }}>
                                {urgency.label}
                            </Typography>
                        </Grid>
                    ) : props.dueDate ? (
                        <Typography variant="caption" color="quaternary" sx={{ mt: 1.5, display: "block" }}>
                            {`${intl("dueDate")}: ${props.dueDate}`}
                        </Typography>
                    ) : null}

                    {props.showImpactedClients && (
                        <Grid sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                            <GroupOutlined sx={{ fontSize: 16, color: "neutral.light" }} />
                            <Typography variant="caption" color="quaternary">
                                {`${props.impactedClients} ${intl("impactedClients").toLowerCase()}`}
                            </Typography>
                        </Grid>
                    )}

                    {/* Ações */}
                    {props.showImpactedClients && (
                        <Grid sx={{ mt: compact ? 1.25 : 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                            {showSeeClientsButton && (
                                <Button
                                    color="primary"
                                    variant="text"
                                    size="small"
                                    onClick={handleClickSeeClients}
                                    disabled={!props.impactedClientsArray || props.impactedClientsArray.length === 0}
                                    sx={{ justifyContent: "flex-start", px: 0, alignSelf: "flex-start" }}
                                >
                                    {`${intl("seeClients")} →`}
                                </Button>
                            )}
                            <Button
                                color="primary"
                                variant="contained"
                                size="small"
                                fullWidth
                                onClick={handleClickAIButton}
                                startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
                            >
                                {intl("generateStrategy")}
                            </Button>
                            {props.monitorButton ?? null}
                        </Grid>
                    )}
                    {props.footer ? <Grid sx={{ mt: 1, width: "100%" }}>{props.footer}</Grid> : null}
                </Grid>
            </Grid>
        </StyledObjectiveCard>
    )
}

export { ObjectiveCard }
